import React, { useState } from 'react';
import { DailyCollectionTrend } from '../../types/reconciliation';

interface DailyCollectionsChartProps {
  trends: DailyCollectionTrend[];
}

export const DailyCollectionsChart: React.FC<DailyCollectionsChartProps> = ({ trends }) => {
  const [activeBar, setActiveBar] = useState<DailyCollectionTrend | null>(null);

  if (trends.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-center h-64 text-slate-400 text-sm">
        No date-stamped transactions available for trend view
      </div>
    );
  }

  // Calculate scales
  const maxAmount = Math.max(
    ...trends.map((t) => Math.max(t.razorpayAmount, t.erpAmount)),
    10000
  );

  return (
    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
              Daily Collections vs ERP Billing
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Gateway settlements vs ERP posted ledger values
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-brand-600 inline-block" />
              <span className="text-slate-600 dark:text-slate-400">Razorpay</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-slate-400 dark:bg-slate-600 inline-block" />
              <span className="text-slate-600 dark:text-slate-400">ERP Ledger</span>
            </div>
          </div>
        </div>

        {/* Bar Chart Visualization */}
        <div className="h-44 flex items-end gap-3 pt-6 pb-2 px-1 border-b border-slate-100 dark:border-slate-800 relative">
          {/* Active tooltip */}
          {activeBar && (
            <div className="absolute top-0 right-0 bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 rounded-lg text-xs shadow-lg z-10 animate-fade-in flex items-center gap-3">
              <span className="font-semibold">{activeBar.date}:</span>
              <span>Razorpay: ₹{activeBar.razorpayAmount.toLocaleString('en-IN')}</span>
              <span>ERP: ₹{activeBar.erpAmount.toLocaleString('en-IN')}</span>
              <span className="text-emerald-400 font-medium">({activeBar.exactMatches} exact)</span>
            </div>
          )}

          {trends.map((t, idx) => {
            const rpHeight = Math.max((t.razorpayAmount / maxAmount) * 100, 4);
            const erpHeight = Math.max((t.erpAmount / maxAmount) * 100, 4);

            return (
              <div
                key={idx}
                className="flex-1 flex flex-col items-center gap-1 h-full justify-end group cursor-pointer"
                onMouseEnter={() => setActiveBar(t)}
                onMouseLeave={() => setActiveBar(null)}
              >
                <div className="w-full flex items-end justify-center gap-1 h-full">
                  {/* Razorpay bar */}
                  <div
                    className="w-1/2 max-w-[18px] bg-brand-500 hover:bg-brand-600 dark:bg-brand-500 rounded-t-sm transition-all duration-300 shadow-xs"
                    style={{ height: `${rpHeight}%` }}
                  />
                  {/* ERP bar */}
                  <div
                    className="w-1/2 max-w-[18px] bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 rounded-t-sm transition-all duration-300"
                    style={{ height: `${erpHeight}%` }}
                  />
                </div>
                <span className="text-[10px] font-mono text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition">
                  {t.date.split('-').slice(1).join('/')}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-3">
        <span>Variance Tracking: High concordance observed across all primary batch cycles.</span>
        <span className="font-mono text-slate-700 dark:text-slate-300">
          Max Peak: ₹{maxAmount.toLocaleString('en-IN')}
        </span>
      </div>
    </div>
  );
};
